// frontend/src/site/builder/sections/ImoveisDestaqueForm.jsx
import React, { useMemo } from 'react';

function Label({ children }) {
  return <div className="text-sm font-medium text-gray-800">{children}</div>;
}
function Input({ value, onChange, placeholder = '' }) {
  return (
    <input
      value={value || ''}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200"
    />
  );
}
function Textarea({ value, onChange, placeholder = '' }) {
  return (
    <textarea
      value={value || ''}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 min-h-[90px]"
    />
  );
}
function Select({ value, onChange, children }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange?.(e.target.value)}
      className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white"
    >
      {children}
    </select>
  );
}
function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange?.(!checked)}
      className="flex items-center justify-between w-full"
    >
      <div className="text-sm text-gray-800">{label}</div>
      <div
        className={`w-12 h-7 rounded-full relative transition ${
          checked ? 'bg-black' : 'bg-gray-300'
        }`}
      >
        <span
          className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition ${
            checked ? 'translate-x-5' : ''
          }`}
        />
      </div>
    </button>
  );
}

/* =========================
   Helpers
========================= */
const LIMIT = 6;

function toInt(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function parseIds(textOrArray) {
  // aceita: array (novo) ou string "id1, id2"
  if (Array.isArray(textOrArray)) return textOrArray.filter(Boolean);

  const raw = String(textOrArray || '');
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function idsToText(idsArray) {
  const arr = Array.isArray(idsArray) ? idsArray : [];
  return arr.filter(Boolean).join(', ');
}

function clampQuantidade(n) {
  const v = toInt(n, LIMIT);
  // só 3 ou 6
  if (v <= 3) return 3;
  return LIMIT;
}

export default function ImoveisDestaqueForm({
  value = {},
  onChange,
  sectionId = '', // 'imoveis' | 'terrenos'
  modelId = '', // opcional
}) {
  const v = value || {};

  // ✅ terrenos é “sempre recentes”, sem modo manual
  const isTerrenosSection =
    String(sectionId || '').toLowerCase() === 'terrenos';

  // ✅ modo novo: recentes | selecionados
  const modo = isTerrenosSection
    ? 'recentes'
    : v.modo === 'selecionados' || v.modo === 'recentes'
      ? v.modo
      : // compat com legado automatico/manual
        v.modo === 'manual'
        ? 'selecionados'
        : 'recentes';

  // ✅ quantidade nova: 3 ou 6 (máx 6)
  const quantidade = clampQuantidade(v.quantidade ?? v.maxItems ?? LIMIT);

  // ✅ ids como array (mas editor pode ter string do legado)
  const idsArrRaw = useMemo(() => parseIds(v.ids), [v.ids]);
  const idsArr = useMemo(() => idsArrRaw.slice(0, LIMIT), [idsArrRaw]);
  const idsText = useMemo(() => idsToText(idsArr), [idsArr]);
  const idsCount = idsArr.length;

  function patch(p) {
    onChange?.(p);
  }

  function patchIdsFromText(text) {
    const nextArr = parseIds(text).slice(0, LIMIT);
    patch({
      ids: nextArr,
      // se colocou ids, faz sentido virar selecionados
      modo: nextArr.length ? 'selecionados' : 'recentes',
    });
  }

  const showButton = v.mostrarBotaoVerTodos !== false && v.showButton !== false;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label>Título</Label>
          <Input
            value={v.titulo}
            onChange={(x) => patch({ titulo: x })}
            placeholder={
              isTerrenosSection ? 'Ex: Terrenos' : 'Ex: Imóveis em Destaque'
            }
          />
        </div>

        <div className="md:col-span-2">
          <Label>Subtítulo</Label>
          <Textarea
            value={v.subtitulo}
            onChange={(x) => patch({ subtitulo: x })}
            placeholder={
              isTerrenosSection
                ? 'Ex: Lotes e terrenos disponíveis...'
                : 'Ex: Seleção de oportunidades...'
            }
          />
        </div>

        {/* ✅ Modo só existe pra imoveis (não pra terrenos) */}
        {!isTerrenosSection ? (
          <div>
            <Label>Modo de seleção</Label>
            <Select value={modo} onChange={(x) => patch({ modo: x })}>
              <option value="recentes">
                Se não tiver destaque, mostrar recentes
              </option>
              <option value="selecionados">Selecionados (máx 6)</option>
            </Select>
            <div className="mt-1 text-xs text-gray-500">
              Regra: se tiver IDs selecionados, usa eles. Se não, mostra os mais
              recentes.
            </div>
          </div>
        ) : (
          <div>
            <Label>Modo</Label>
            <div className="mt-2 text-sm text-gray-700">
              Terrenos sempre mostram os <b>últimos 6</b>.
            </div>
          </div>
        )}

        <div>
          <Label>Quantidade (máx {LIMIT})</Label>
          <Select
            value={String(quantidade)}
            onChange={(x) => patch({ quantidade: clampQuantidade(x) })}
          >
            <option value="3">3</option>
            <option value="6">6</option>
          </Select>
          <div className="mt-1 text-xs text-gray-500">
            Mantemos no máximo {LIMIT} por performance e layout.
          </div>
        </div>

        {/* ✅ IDs só aparecem quando modo=selecionados e não for terrenos */}
        {!isTerrenosSection && modo === 'selecionados' ? (
          <div className="md:col-span-2">
            <div className="flex items-end justify-between gap-3">
              <Label>IDs dos imóveis (separados por vírgula)</Label>
              <div className="text-xs text-gray-500">
                <b className={idsCount >= LIMIT ? 'text-amber-700' : ''}>
                  {idsCount}/{LIMIT}
                </b>
              </div>
            </div>

            <Textarea
              value={idsText}
              onChange={(x) => patchIdsFromText(x)}
              placeholder="id1, id2, id3..."
            />

            <div className="mt-2 text-xs text-gray-500">
              {idsCount >= LIMIT
                ? `Limite atingido (${LIMIT}). IDs extras são ignorados.`
                : `Você pode selecionar até ${LIMIT} imóveis.`}
            </div>
          </div>
        ) : null}
      </div>

      <div className="rounded-xl border border-gray-200 p-4 space-y-3">
        <Switch
          checked={showButton}
          onChange={(x) =>
            patch({
              mostrarBotaoVerTodos: x,
              showButton: x, // compat
            })
          }
          label={
            isTerrenosSection
              ? 'Mostrar botão “Ver todos os terrenos”'
              : 'Mostrar botão “Ver todos os imóveis”'
          }
        />

        {showButton ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Texto do botão</Label>
              <Input
                value={v.textoBotaoVerTodos || v.buttonLabel}
                onChange={(x) =>
                  patch({
                    textoBotaoVerTodos: x,
                    buttonLabel: x, // compat
                  })
                }
                placeholder={
                  isTerrenosSection
                    ? 'Ex: Ver todos os terrenos'
                    : 'Ex: Ver todos os imóveis'
                }
              />
            </div>

            <div>
              <Label>Ação do botão</Label>
              <Select
                value={v.buttonAction || 'imoveis'}
                onChange={(x) => patch({ buttonAction: x })}
              >
                <option value="imoveis">
                  {isTerrenosSection
                    ? 'Ir para /imoveis?tipo=terreno'
                    : 'Ir para /imoveis'}
                </option>
                <option value="url">Abrir URL</option>
              </Select>
            </div>

            {v.buttonAction === 'url' ? (
              <div className="md:col-span-2">
                <Label>URL</Label>
                <Input
                  value={v.buttonUrl}
                  onChange={(x) => patch({ buttonUrl: x })}
                  placeholder="https://..."
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
